import { useQuery } from '@apollo/client';
import { config as FAConfig } from '@fortawesome/fontawesome-svg-core';
import { Loader, ThemeProvider } from '@gasbuddy/react-components';
import { useAnalytics, useDataLayer } from '@gasbuddy/react-hooks';
import loadable from '@loadable/component';
import gql from 'graphql-tag';
import { useEffect, useMemo } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router-dom';
import i18n from '../../../client/i18n';
import { isWindowAvailable } from '../../../lib/utils';
import ReactGTM from '../../../lib/utils/gtm';
import hasDeletedAccount from '../../../lib/utils/hasDeletedAccount';
import logEvent from '../../../lib/utils/logEvent';
import FeaturesContext from '../../context/features';
import GlobalContext from '../../context/global';
import { Provider as PageProvider } from '../../context/page';
import ErrorBoundary from '../ErrorBoundary';
import PageLayout from '../PageLayout/PageLayout';
import PrivateRoute from '../PrivateRoute';

FAConfig.autoAddCss = false;

const LOCALYTICS_CUSTOM_DIMENSIONS = {
  USER_ID: 14,
  USER_NAME: 15,
};

export const GET_PAGES = gql`
  query GetPages{
    pages {
      ... on CMSAboutUsPage {
        url
      }
      ... on CMSLandingPage {
        url
      }
    }
  }
`;

export const FETCH_GLOBAL_CONTEXT = gql`
  query FetchGlobalContext{
    config {
      consumerHost
      env
      googleAppId
      googleMapsKey
      gtm {
        id
      }
      identityHost
      imagesHost
      isMobile
      localyticsKey
      localyticsDomain
      mapboxMapKey
      payHost
      publicPath
      recaptchaSiteKey
      trackerHost
    }
    features {
      n
      v
      p
    }
    profile {
      accountId
      allTimeCredits
      canSetMemberName
      consecutiveDays
      deletionStatus {
        endDate
        status
      }
      email
      emailHash
      favoriteStationsCount
      gBAge
      isLoggedIn
      isPayUser
      isPwGBUser
      isPwGBPlusUser
      isTrusted
      joinDateText
      localSite
      memberName
      overallRank
      profileImageUrl
      requestedDNS
      userVehiclesQuickInfo {
        featuredVehicle {
          fuelLogCount
          guid
          make
          model
          nickname
          year
        }
        vehicleCount
      }
    }
    geoIpInfo {
      locality
      state
      zip
      country
    }
    mailingAddress {
      country
      firstName
      lastName
      line1
      line2
      locality
      postalCode
      region
    }
  }
`;

export default function Router() {
  const { t } = useTranslation('miscellaneous');
  const { data, loading } = useQuery(GET_PAGES);
  const { data: globalContextData, loading: isLoadingContext } = useQuery(FETCH_GLOBAL_CONTEXT);
  const { pushOneTrustConsent } = useDataLayer();
  const cmsUrls = data?.pages.map(({ url }) => url) || [];
  const { gtm, localyticsKey, localyticsDomain, env } = globalContextData?.config || {};
  const { accountId, deletionStatus, memberName } = globalContextData?.profile || {};
  const isProduction = env === 'production';

  useEffect(() => {
    if (isWindowAvailable()) {
      if (gtm) {
        ReactGTM.initialize(gtm, accountId);
      }
    }
  }, [gtm, accountId]);

  // Handle OneTrust consent tracking
  useEffect(() => {
    if (!isWindowAvailable()) {
      return;
    }

    // Push consent status when OneTrust is ready
    const originalOptanonWrapper = window.OptanonWrapper || (() => {});
    window.OptanonWrapper = function OptanonWrapper() {
      // Call original function if it exists
      originalOptanonWrapper();
      // Push OneTrust consent status to data layer
      pushOneTrustConsent();
    };

    // Also push on initial load if OneTrust is already available
    if (window.OnetrustActiveGroups) {
      pushOneTrustConsent();
    }
  }, [pushOneTrustConsent]);

  // Make sure we reset the username modal flag in local storage if we ever have a logged out user
  useEffect(() => {
    if (!memberName && typeof localStorage !== 'undefined') {
      if (localStorage.getItem('lastShownMemberNameModal') !== 'none') {
        localStorage.setItem('lastShownMemberNameModal', 'none');
      }
    }
  }, [memberName]);

  // TODO: Update analytics hook to support SSR
  const analyticsConfig = isWindowAvailable() && {
    localytics: {
      domain: localyticsDomain,
      key: localyticsKey,
      allowLogs: !isProduction,
    },
  };

  const analytics = useAnalytics(analyticsConfig);

  const globalContextValue = useMemo(() => ({
    ...globalContextData,
    isLoadingContext,
    analytics,
  }), [analytics, globalContextData, isLoadingContext]);

  const loadableOpts = useMemo(() => ({
    fallback: (
      <Loader size="lg">
        {t('status.loadingPage')}
      </Loader>
    ),
  }), [t]);

  if (analytics) {
    analytics.on('event', (eventName, eventAttributes) => {
      logEvent(`Localytics:${eventName}`, eventAttributes);
    });

    if (accountId) {
      analytics.setCustomDimension(LOCALYTICS_CUSTOM_DIMENSIONS.USER_ID, accountId);
    }

    if (memberName) {
      analytics.identifyUser({
        id: memberName,
      });
      analytics.setCustomDimension(LOCALYTICS_CUSTOM_DIMENSIONS.USER_NAME, memberName);
    }
  }

  // Remove all listeners when the component unmounts
  useEffect(() => () => analytics.removeAllListeners('event'), [analytics]);

  // In case a user with a deleted account somehow manages to navigate to a page, redirect them to logout
  if (hasDeletedAccount(deletionStatus)) {
    return (
      <Redirect to="/logout" />
    );
  }

  // Don't allow a landing page to return a 404 on initial render
  if (loading) {
    return null;
  }

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <FeaturesContext.Provider value={globalContextData?.features}>
          <GlobalContext.Provider value={globalContextValue}>
            <PageProvider>
              <ErrorBoundary>
                <Switch>
                  <Route exact path="/account/confirm" component={loadable(() => import('../AccountAction'))} />
                  {cmsUrls.map(cmsUrl => (
                    <Route key={cmsUrl} component={loadable(() => import('../CMSPage'))} exact path={`/${cmsUrl}`} />
                  ))}
                  <Route>
                    <PageLayout>
                      <Switch>
                        <Route exact path="/" component={loadable(() => import('../HomePage'), loadableOpts)} />
                        <Route exact path="/about/timeline" component={loadable(() => import('../TimelinePage'), loadableOpts)} />
                        <PrivateRoute exact path="/account/favorite-lists" component={loadable(() => import('../FavoriteStationsListsPage'), loadableOpts)} />
                        <PrivateRoute exact path="/account/favorite-lists/:favoriteListId" component={loadable(() => import('../FavoritesListPage'), loadableOpts)} />
                        <PrivateRoute exact path="/account/fuel-log-book" component={loadable(() => import('../RedirectToFuelLogBook'), loadableOpts)} />
                        <PrivateRoute exact path="/account/preferences" component={loadable(() => import('../UserEmailPreferencesPage'), loadableOpts)} />
                        <Route exact path="/account/profile" component={loadable(() => import('../ProfilePage'), loadableOpts)} />
                        <Route exact path="/account/savings" component={loadable(() => import('../PaySavingsPage'), loadableOpts)} />
                        <PrivateRoute exact path="/account/vehicles" component={loadable(() => import('../MyVehiclesPage'), loadableOpts)} />
                        <PrivateRoute exact path="/account/vehicles/:guid" component={loadable(() => import('../MyVehicleProfilePage'), loadableOpts)} />
                        <PrivateRoute exact path="/account/vehicles/:guid/fuel-log-book" component={loadable(() => import('../FuelLogBookPage'), loadableOpts)} />
                        <PrivateRoute exact path="/account/vehicles/:guid/fuel-log-book/entry/:fuelLogGuid?" component={loadable(() => import('../FuelLogEntryPage'), loadableOpts)} />
                        <Route exact path="/app" component={loadable(() => import('../AppPage'), loadableOpts)} />
                        <Route exact path="/brands/:brandKey" component={loadable(() => import('../BrandPage'), loadableOpts)} />
                        <Route exact path="/contest/gas-card-give-away" component={loadable(() => import('../EnterToWinPage'), loadableOpts)} />
                        <Route path="/disclaimer/:identifier?" component={loadable(() => import('../LegalPage'), loadableOpts)} />
                        <Route exact path="/do-not-sell-my-personal-information" component={loadable(() => import('../DoNotSellMyPI'), loadableOpts)} />
                        <Route exact path="/emailpreferences" component={loadable(() => import('../VisitorPreferencesPage'), loadableOpts)} />
                        <Route exact path="/gasback" component={loadable(() => import('../GasbackLanding'), loadableOpts)} />
                        <Route exact path="/gasprices" component={loadable(() => import('../RegionSelectPage'), loadableOpts)} />
                        <Route exact path="/gasprices/:region/:area?/:countyId?" component={loadable(() => import('../RegionalGasPrices'), loadableOpts)} />
                        <Route exact path="/home" component={loadable(() => import('../SearchResultsPage'), loadableOpts)} />
                        <Route exact path="/leaderboards" component={loadable(() => import('../LeaderboardsPage'), loadableOpts)} />
                        <PrivateRoute exact path="/station-edits" component={loadable(() => import('../MasterStationListPage'), loadableOpts)} />
                        <Route exact path="/newsroom" component={loadable(() => import('../NewsroomPage'), loadableOpts)} />
                        <Route exact path="/newsroom/inthenews" component={loadable(() => import('../NewsArticlesPage'), loadableOpts)} />
                        <Route exact path="/newsroom/pressrelease/:legacyId" component={loadable(() => import('../PressReleaseLookup'), loadableOpts)} />
                        <Route exact path="/newsroom/pressrelease/:year/:month/:day/:identifier" component={loadable(() => import('../PressReleasePage'), loadableOpts)} />
                        <Route exact path="/newsroom/pressreleases" component={loadable(() => import('../PressReleasesPage'), loadableOpts)} />
                        <Route exact path="/pay/plus" component={loadable(() => import('../PayPlusPage'), loadableOpts)} />
                        <Route path="/privacy/:identifier?" component={loadable(() => import('../LegalPage'), loadableOpts)} />
                        <Route path="/regionstats" component={loadable(() => import('../RegionPriceStatisticsPage'), loadableOpts)} />
                        <Route exact from="/station/:brandName/:regionCode/:regionDescription/:stationId([0-9]+)" to="/station/:stationId" />
                        <Route exact path="/station/:stationId([0-9]+)" component={loadable(() => import('../Station'), loadableOpts)} />
                        <Route path="/terms/:identifier?" component={loadable(() => import('../LegalPage'), loadableOpts)} />
                        <Route path="/tripcostcalculator" component={loadable(() => import('../TripCostCalculatorPage'), loadableOpts)} />
                        <Route exact path="/vehicles/recalls/results" component={loadable(() => import('../VehicleRecallSearchResultsPage'), loadableOpts)} />
                        <Route exact path="/vehicles/recalls/search" component={loadable(() => import('../VehicleRecallSearchPage'), loadableOpts)} />
                        <Route component={loadable(() => import('../Unknown'), loadableOpts)} />
                      </Switch>
                    </PageLayout>
                  </Route>
                </Switch>
              </ErrorBoundary>
            </PageProvider>
          </GlobalContext.Provider>
        </FeaturesContext.Provider>
      </ThemeProvider>
    </I18nextProvider>
  );
}
